import React, { useState, useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import Sidebar from './Sidebar';
import Header from './Header';

const MainLayout: React.FC = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [clientSelected, setClientSelected] = useState(false);
  const location = useLocation();

  // Check for client selection change
  useEffect(() => {
    const checkClientSelection = () => {
      const selectedClient = sessionStorage.getItem('selectedClient');
      setClientSelected(!!selectedClient);
    };
    
    checkClientSelection();
    
    // Listen for storage changes and custom events
    const handleStorageChange = () => checkClientSelection();
    const handleClientSelected = () => checkClientSelection();
    
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('clientSelected', handleClientSelected);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('clientSelected', handleClientSelected);
    };
  }, [location.pathname]);

  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  const handleMenuClick = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const handleSidebarClose = () => {
    setSidebarOpen(false);
  };

  const handleToggleCollapse = () => {
    setIsCollapsed(!isCollapsed);
  };

  // Get page title and subtitle based on current route
  const getPageInfo = () => {
    const { pathname } = location;

    switch (pathname) {
      case '/dashboard':
        return { title: 'Dashboard', subtitle: 'Your underwriting overview for today' };
      case '/carrier':
        return { title: 'Carrier Dashboard', subtitle: 'Quotes and team for your carrier' };
      case '/carrier/users':
        return { title: 'Carrier Users', subtitle: 'Add, edit and control access for your team' };
      case '/my-quotes':
        return { title: 'SubCarrier User Dashboard', subtitle: 'Submissions assigned to you' };
      case '/clients':
        return { title: 'Clients', subtitle: 'Manage your clients and create new quotes' };
      case '/quote-history':
        return { title: 'Quote History', subtitle: 'View and manage all your insurance quotes' };
      case '/new-quote':
        if (clientSelected) {
          return { title: 'New Quote', subtitle: 'Create a new insurance quote for your client' };
        } else {
          return { title: 'Select a Client', subtitle: 'Choose a client to create a quote for' };
        }
      default:
        if (pathname.endsWith('/data-room')) {
          return { title: 'Data Room', subtitle: 'Complete the checklist before binding' };
        }
        if (pathname.startsWith('/quote-history/') || pathname.startsWith('/carrier/quote/') || pathname.startsWith('/my-quotes/quote/')) {
          return { title: 'Quote Details', subtitle: 'Full submission breakdown' };
        }
        return { title: '', subtitle: '' };
    }
  };

  const { title: pageTitle, subtitle: pageSubtitle } = getPageInfo();

  const pageVariants = {
    initial: { opacity: 0, y: 10 },
    animate: { opacity: 1, y: 0 }
  };

  return (
    <div className="main-layout">
      <Sidebar 
        isOpen={sidebarOpen} 
        isCollapsed={isCollapsed}
        onClose={handleSidebarClose}
        onToggleCollapse={handleToggleCollapse}
      />
      <div className={`main-layout__content-wrapper ${isCollapsed ? 'main-layout__content-wrapper--collapsed' : ''}`}>
        <Header 
          onMenuClick={handleMenuClick} 
          isCollapsed={isCollapsed} 
          pageTitle={pageTitle}
          pageSubtitle={pageSubtitle}
        />
        {/* No AnimatePresence exit: an exiting <Outlet /> re-renders the *new* route, mounting the page twice
            (and doubling its API calls). */}
        <motion.main
          key={location.pathname}
          className="main-layout__content"
          variants={pageVariants}
          initial="initial"
          animate="animate"
          transition={{ duration: 0.2, ease: 'easeOut' }}
        >
          <Outlet />
        </motion.main>
      </div>
    </div>
  );
};

export default MainLayout;
